/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { ISchemaStringWithConstConstItem, isModelValueRequired } from "@adapptio/lib-blueprint";
import { FieldColor, FieldGroup } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { useTranslate } from "@adapptio/lib-components";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { IInspectorControl_Color, TInspectorControlProps } from "../InspectorControlTypes";

export interface IInspectorControlColorProps extends TInspectorControlProps<IInspectorControl_Color> {}

export const InspectorControlColor: React.FC<IInspectorControlColorProps> = ({ model: rootModel }) => {
	const t = useTranslate();
	const { model: valueModel, validationErrors } = useBlueprintModel(
		rootModel.value.constant,
		"constString"
	);

	const required = isModelValueRequired(rootModel) || isModelValueRequired(valueModel);

	const inspectorContext = useEditorInspectorContext();

	const constants = useMemo(() => {
		return rootModel.schema.opts.constants.map((item) => {
			return {
				label: t("editor", item.label),
				value: item.value
			} as ISchemaStringWithConstConstItem;
		});
	}, [ rootModel ]);

	const handleChange = useCallback(
		(value: string) => {
			if (!valueModel) {
				return;
			}

			valueModel.schema.setValue(valueModel, value, true);
		},
		[ valueModel ]
	);

	if (!valueModel) {
		return <InvalidModelNode model={valueModel} />;
	}

	return (
		<FieldGroup grow>
			<FieldColor
				value={valueModel.value}
				constants={constants}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
				clearable={!required}
			/>
		</FieldGroup>
	);
};
